import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    IoPersonOutline,
    IoLockClosedOutline,
    IoBusinessOutline,
    IoEyeOutline,
    IoEyeOffOutline
} from "react-icons/io5";

import logo from '../../resources/logo.png';
import LoginBackgroundMobile from '../../resources/bg_new_mobile.png';
import LoginBackground from '../../resources/bg_new.png';
import UtilityService from "../../rest/utilityService";

const PasswordResetPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const inputContainerStyle = "relative flex items-center mb-4";
    const iconStyle = "absolute left-3 text-gray-400 text-xl z-10";
    const inputStyleWithIcon = "w-full pl-10 pr-10 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-custom_blue focus:ring-2 focus:ring-custom_blue/20 focus:outline-none transition-all duration-200 placeholder-gray-400";

    const tokenFromUrl = searchParams.get("token");
    const loginFromUrl = searchParams.get("login") || "";
    const clientCodeFromUrl = searchParams.get("clientCode") || "";

    const formik = useFormik({
        initialValues: {
            clientCode: clientCodeFromUrl,
            login: loginFromUrl,
            password: "",
            confirmPassword: ""
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            clientCode: Yup.string().required("Código do cliente é obrigatório"),
            login: Yup.string().required("Login é obrigatório"),
            password: Yup.string()
                .required("Senha é obrigatória")
                .min(8, "Mínimo de 8 caracteres")
                .matches(/[A-Z]/, "Precisa de uma letra maiúscula")
                .matches(/[a-z]/, "Precisa de uma letra minúscula")
                .matches(/[0-9]/, "Precisa de um número")
                .matches(/[!@#$%^&*(),.?":{}|<>]/, "Precisa de um símbolo"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], 'As senhas devem ser iguais')
                .required('Confirmação de senha é obrigatória'),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {
                    token: tokenFromUrl,
                    clientCode: values.clientCode,
                    login: values.login,
                    newPassword: values.password
                };

                await UtilityService.resetPassword(payload);

                toast.success("Senha redefinida com sucesso!");
                setTimeout(() => {
                    navigate('/login');
                }, 2000);

            } catch (err) {
                console.error(err);
                const msg = err.response?.data?.message || "Erro ao redefinir senha.";
                toast.error(typeof err.response?.data === 'string' ? err.response.data : msg);
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <div className='relative min-h-screen w-full overflow-hidden font-sans bg-gray-900'>
            <div className="absolute inset-0 z-0">
                <img src={LoginBackgroundMobile} alt='bg' className='absolute inset-0 object-cover w-full h-full blur-[3px] scale-105 block md:hidden' />
                <img src={LoginBackground} alt='bg' className='absolute inset-0 object-cover w-full h-full blur-[3px] scale-105 hidden md:block' />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/60"></div>
            </div>

            <div className='flex items-center justify-center min-h-screen relative z-10 p-4 py-12'>
                <div className='w-full max-w-md bg-white/95 sm:bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 sm:p-10 border border-white/20 transform transition-all'>

                    <div className="flex flex-col items-center mb-6">
                        <img src={logo} alt='Logo Foodz' className='w-32 h-auto object-contain mb-2 drop-shadow-sm' />
                        <h2 className="text-xl font-bold text-gray-800 mt-2 text-center">Redefinir Senha</h2>
                        <p className="text-gray-500 text-sm text-center mt-1">Informe suas credenciais e a nova senha.</p>
                    </div>

                    <form onSubmit={formik.handleSubmit} className='flex flex-col'>

                        <div className={inputContainerStyle}>
                            <IoBusinessOutline className={iconStyle} />
                            <input
                                type="text"
                                name="clientCode"
                                {...formik.getFieldProps('clientCode')}
                                placeholder="Código do Cliente"
                                className={`${inputStyleWithIcon} ${formik.touched.clientCode && formik.errors.clientCode ? "border-red-400" : ""}`}
                                readOnly={!!clientCodeFromUrl}
                            />
                        </div>
                        {formik.touched.clientCode && formik.errors.clientCode && (
                            <div className="mb-3 -mt-3 ml-1 text-red-500 text-xs">{formik.errors.clientCode}</div>
                        )}

                        <div className={inputContainerStyle}>
                            <IoPersonOutline className={iconStyle} />
                            <input
                                type="text"
                                name="login"
                                {...formik.getFieldProps('login')}
                                placeholder="Seu Login"
                                className={`${inputStyleWithIcon} ${formik.touched.login && formik.errors.login ? "border-red-400" : ""}`}
                                readOnly={!!loginFromUrl}
                            />
                        </div>
                        {formik.touched.login && formik.errors.login && (
                            <div className="mb-3 -mt-3 ml-1 text-red-500 text-xs">{formik.errors.login}</div>
                        )}

                        <div className={inputContainerStyle}>
                            <IoLockClosedOutline className={iconStyle} />
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                {...formik.getFieldProps('password')}
                                placeholder="Nova Senha"
                                className={`${inputStyleWithIcon} ${formik.touched.password && formik.errors.password ? "border-red-400" : ""}`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 text-gray-400 hover:text-custom_blue z-10"
                            >
                                {showPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                            </button>
                        </div>
                        {formik.touched.password && formik.errors.password && (
                            <div className="mb-3 -mt-3 ml-1 text-red-500 text-xs">{formik.errors.password}</div>
                        )}

                        <div className={inputContainerStyle}>
                            <IoLockClosedOutline className={iconStyle} />
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                {...formik.getFieldProps('confirmPassword')}
                                placeholder="Confirmar Senha"
                                className={`${inputStyleWithIcon} ${formik.touched.confirmPassword && formik.errors.confirmPassword ? "border-red-400" : ""}`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 text-gray-400 hover:text-custom_blue z-10"
                            >
                                {showConfirmPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                            </button>
                        </div>
                        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                            <div className="mb-3 -mt-3 ml-1 text-red-500 text-xs">{formik.errors.confirmPassword}</div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !formik.isValid}
                            className="w-full py-3.5 mt-4 rounded-xl bg-gradient-to-r from-custom_blue to-[#1e4585] hover:to-custom_blue shadow-lg shadow-custom_blue/30 font-bold tracking-wide text-white transform active:scale-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processando...
                                </>
                            ) : "DEFINIR NOVA SENHA"}
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="mt-6 text-sm text-gray-500 hover:text-custom_blue transition-colors text-center"
                        >
                            Voltar para o Login
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PasswordResetPage;